import { useEffect, useState, type ReactNode } from 'react';
import type { Cotizacion, DesgloseTiempo } from '@laser/core';
import { useActualizarTrabajo, useAprobar, useCatalogo } from '../../api/hooks';
import type { TrabajoDetalle } from '../../api/tipos';
import { Confirmar, EntradaPesos, Insignia, mensajeError, useAvisar } from '../../componentes/ui';
import { puedeAprobar } from '../../lib/estados';
import { fechaCorta } from '../../lib/fechas';
import { diferenciaRelativa, duracion, medidas, pesos, signoPct } from '../../lib/formato';
import { totalACobrar } from '../../lib/trabajo';

const TIEMPOS: [keyof DesgloseTiempo, string][] = [
  ['corteS', 'Corte'], ['marcadoS', 'Marcado'], ['grabadoS', 'Grabado'], ['perforacionS', 'Perforación'], ['trasladoS', 'Traslado'],
];

/** Lo que no es máquina, material, preparación ni urgente: margen, mínimo y redondeo. */
export const margenDe = (c: Cotizacion) => c.totalCentavos - c.maquinaCentavos - c.materialCentavos - c.preparacionCentavos - c.urgenteCentavos;

export function Cotizaciones({ trabajo, cambiosPendientes }: { trabajo: TrabajoDetalle; cambiosPendientes: boolean }) {
  const cat = useCatalogo();
  const avisar = useAvisar();
  const actualizar = useActualizarTrabajo(trabajo.id);
  const aprobar = useAprobar(trabajo.id);
  const [precio, setPrecio] = useState<number | null>(trabajo.precioFinalCentavos);
  const [confirmar, setConfirmar] = useState(false);
  useEffect(() => { setPrecio(trabajo.precioFinalCentavos); }, [trabajo.precioFinalCentavos]);

  const o = trabajo.cotizacionOriginal;
  const f = trabajo.cotizacionFinal;
  const cols = f ? [o, f] : [o];
  const maq = (c: Cotizacion) => cat.maquina.get(c.maquinaId)?.nombre ?? '—';
  const mat = (c: Cotizacion) => { const m = cat.material.get(c.materialId); return m ? `${m.nombre} ${m.espesorMm} mm` : '—'; };
  const aCobrar = totalACobrar(trabajo);
  const precioCambiado = precio !== trabajo.precioFinalCentavos;

  const fila = (concepto: ReactNode, valores: ReactNode[], clase?: string, distinto?: boolean, clave?: string) => (
    <tr className={clase} key={clave}>
      <td>{concepto}</td>
      {valores.map((v, i) => <td key={i} className="num" style={distinto && i === 1 ? { fontWeight: 700 } : undefined}>{v}</td>)}
    </tr>
  );

  const aprobarYa = () => aprobar.mutate(undefined, {
    onSuccess: () => { setConfirmar(false); avisar('Link de pago creado y enviado al cliente', 'ok'); },
    onError: (e) => avisar(mensajeError(e), 'bad'),
  });

  return (
    <section className="tarjeta" aria-labelledby="cot-t">
      <div className="fila fila--entre">
        <h2 id="cot-t">Cotización</h2>
        {o.esEstimado && <Insignia tono="neutral" title="El cliente vio un precio estimado">estimado al cliente</Insignia>}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="comparar">
          <thead>
            <tr><th>Concepto</th><th className="num">Original</th>{f && <th className="num">Final</th>}</tr>
          </thead>
          <tbody>
            {TIEMPOS.map(([k, t]) => fila(t, cols.map((c) => duracion(c.tiempo[k])), 'sub', f ? f.tiempo[k] !== o.tiempo[k] : false, k))}
            {fila(<b>Tiempo de láser</b>, cols.map((c) => <b key={c.totalCentavos}>{duracion(c.tiempo.totalS)}</b>))}
            {fila(<span className="celda-2">Máquina<small>{cols.map(maq).join(' → ')}</small></span>, cols.map((c) => pesos(c.maquinaCentavos)), undefined, f ? f.maquinaCentavos !== o.maquinaCentavos : false)}
            {fila(<span className="celda-2">Material<small>{cols.map((c) => `${mat(c)} · ${c.nesting.hojas} ${c.nesting.hojas === 1 ? 'hoja' : 'hojas'}`).join(' → ')}</small></span>,
              cols.map((c) => (c.materialCentavos === 0 ? <span className="muted" key="c">del cliente</span> : pesos(c.materialCentavos))), undefined, f ? f.materialCentavos !== o.materialCentavos : false)}
            {fila('Preparación', cols.map((c) => pesos(c.preparacionCentavos)))}
            {fila('Urgente', cols.map((c) => (c.urgenteCentavos ? pesos(c.urgenteCentavos) : '—')), undefined, f ? f.urgenteCentavos !== o.urgenteCentavos : false)}
            {fila('Margen, mínimo y redondeo', cols.map((c) => pesos(margenDe(c))), 'sub')}
            {fila('Total', cols.map((c) => pesos(c.totalCentavos)), 'total')}
            {f && (
              <tr className="sub"><td>Diferencia</td><td />
                <td className={`num ${f.totalCentavos > o.totalCentavos ? 'cambio-mas' : f.totalCentavos < o.totalCentavos ? 'cambio-menos' : ''}`}>
                  {signoPct(diferenciaRelativa(o.totalCentavos, f.totalCentavos))}
                </td>
              </tr>
            )}
            {fila('Entrega estimada', cols.map((c) => fechaCorta(c.entregaEstimada)), 'sub')}
            {cols.some((c) => c.piezaMaxMm) && fila('Pieza más grande', cols.map((c) => (c.piezaMaxMm ? medidas(c.piezaMaxMm.largoMm, c.piezaMaxMm.cortoMm) : '—')), 'sub')}
          </tbody>
        </table>
      </div>

      {[...o.avisos, ...(f?.avisos ?? [])].length > 0 && (
        <div className="fila" style={{ gap: 6, marginTop: 8 }}>
          {[...new Set([...o.avisos, ...(f?.avisos ?? [])].map((a) => a.mensaje))].map((m) => <Insignia key={m} tono="warn">{m}</Insignia>)}
        </div>
      )}

      <div className="campos" style={{ marginTop: 14, alignItems: 'end' }}>
        <div className="campo">
          <label htmlFor="precio-final">Precio final (manual)</label>
          <EntradaPesos id="precio-final" valor={precio} onCambio={setPrecio} placeholder={String(Math.round((f ?? o).totalCentavos / 100))} />
          <span className="ayuda">Vacío = se cobra la cotización {f ? 'final' : 'original'}.</span>
        </div>
        <div className="fila" style={{ paddingBottom: 18 }}>
          <button type="button" className="btn btn--sm btn--primario" disabled={!precioCambiado || actualizar.isPending}
            onClick={() => actualizar.mutate({ precioFinalCentavos: precio }, { onSuccess: () => avisar('Precio guardado', 'ok'), onError: (e) => avisar(mensajeError(e), 'bad') })}>
            Guardar precio
          </button>
          {trabajo.precioFinalCentavos != null && (
            <button type="button" className="btn btn--sm" disabled={actualizar.isPending}
              onClick={() => actualizar.mutate({ precioFinalCentavos: null }, { onSuccess: () => avisar('Se quitó el precio manual', 'ok') })}>Quitar</button>
          )}
        </div>
      </div>

      <div className="total-caja">
        <span className="meta">A cobrar{trabajo.precioFinalCentavos != null ? ' (manual)' : ''}</span>
        <b>{pesos(aCobrar)}</b>
      </div>

      {trabajo.pago && (
        <div className="pago-caja" style={{ marginTop: 10 }}>
          <div className="fila fila--entre">
            <span className="celda-2"><b>Link de pago {trabajo.pago.folio}</b><small>{trabajo.pago.url}</small></span>
            <Insignia tono={trabajo.pago.estado === 'pagado' ? 'good' : trabajo.pago.estado === 'cancelado' ? 'bad' : 'warn'}>
              {trabajo.pago.estado === 'pagado' ? 'Pagado' : trabajo.pago.estado === 'cancelado' ? 'Cancelado' : 'Pendiente'}
            </Insignia>
          </div>
          <div className="fila">
            <button type="button" className="btn btn--sm" onClick={() => navigator.clipboard?.writeText(trabajo.pago!.url).then(() => avisar('Link copiado'), () => avisar(trabajo.pago!.url))}>Copiar link</button>
            <a className="btn btn--sm" href={trabajo.pago.url} target="_blank" rel="noopener noreferrer">Abrir</a>
          </div>
        </div>
      )}

      {puedeAprobar(trabajo.estado) && (
        <div className="pila" style={{ marginTop: 12, gap: 6 }}>
          {(cambiosPendientes || precioCambiado) && <span className="pendiente">Guarda el precio o recotiza antes de aprobar.</span>}
          <button type="button" className="btn btn--naranja btn--grande" disabled={cambiosPendientes || precioCambiado || aprobar.isPending} onClick={() => setConfirmar(true)}>
            {trabajo.pago ? 'Volver a mandar link de pago' : 'Aprobar y mandar link de pago'} · {pesos(aCobrar)}
          </button>
        </div>
      )}

      <Confirmar abierto={confirmar} titulo="¿Mandar link de pago?" tono="naranja" textoConfirmar={`Mandar link por ${pesos(aCobrar)}`}
        ocupado={aprobar.isPending} onCancelar={() => setConfirmar(false)} onConfirmar={aprobarYa}>
        <p style={{ marginTop: 6 }}>Se crea un link de pago por <b>{pesos(aCobrar)} MXN</b> y se le manda por correo a <b>{trabajo.cliente.correo}</b>.</p>
        <p className="muted" style={{ marginTop: 8 }}>El trabajo pasa a <b>Cotizado</b>. {trabajo.pago ? 'El link anterior deja de ser el vigente.' : ''}</p>
      </Confirmar>
    </section>
  );
}
